import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import HeroSection from './components/HeroSection';
import DogParksSection from './components/DogParksSection';
import TrailsSection from './components/TrailsSection';
import WaterfallsSection from './components/WaterfallsSection';
import RestaurantsSection from './components/RestaurantsSection';
import StaySection from './components/StaySection';
import PetServicesSection from './components/PetServicesSection';
import FAQSection from './components/FAQSection';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://glenfallscabin.com';

export default function DogFriendlyHighlands() {
  return (
    <>
      <title>Dog-Friendly Highlands NC | Pet Friendly Cabins, Trails &amp; Waterfalls</title>
      <meta name="description" content="Your complete guide to dog-friendly Highlands NC — pet friendly cabins, hiking trails, waterfalls, and restaurants that welcome dogs. Plan your visit to Highlands, North Carolina with your dog." />
      <meta name="keywords" content="dog friendly Highlands NC, pet friendly Highlands NC, pet friendly cabins Highlands NC, cabins near waterfalls Highlands NC" />
      <meta property="og:title" content="Dog-Friendly Highlands NC | Pet Friendly Cabins, Trails &amp; Waterfalls" />
      <meta property="og:description" content="Your complete guide to dog-friendly Highlands NC — pet friendly cabins, hiking trails, waterfalls, and restaurants that welcome dogs." />
      <meta name="robots" content="index, follow" />
      <meta name="geo.region" content="US-NC" />
      <meta name="geo.placename" content="Highlands, North Carolina" />
      <meta name="geo.position" content="35.0499;-83.1985" />
      <link rel="canonical" href={`${siteUrl}/dog-friendly-highlands-nc`} />
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": `${siteUrl}/` },
              { "@type": "ListItem", "position": 2, "name": "Dog-Friendly Highlands NC", "item": `${siteUrl}/dog-friendly-highlands-nc` }
            ]
          },
          {
            "@type": "WebPage",
            "name": "Dog-Friendly Highlands NC",
            "description": "A local guide to dog-friendly things to do in Highlands, North Carolina, including pet friendly cabins, hiking trails, waterfalls, and restaurants.",
            "url": `${siteUrl}/dog-friendly-highlands-nc`,
            "about": {
              "@type": "Place",
              "name": "Highlands, North Carolina",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": "Highlands",
                "addressRegion": "NC",
                "addressCountry": "US"
              }
            },
            "mainEntity": {
              "@type": "ItemList",
              "name": "Dog-Friendly Activities in Highlands NC",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Highlands Dog Park", "description": "A well-maintained, fenced off-leash dog park in Highlands, NC." },
                { "@type": "ListItem", "position": 2, "name": "Whiteside Mountain Trail", "description": "Iconic leash-friendly hike with sweeping mountain views near Highlands, NC." },
                { "@type": "ListItem", "position": 3, "name": "Glen Falls Trail", "description": "Scenic forested trail to cascading waterfalls — leashed dogs welcome." },
                { "@type": "ListItem", "position": 4, "name": "Glen Falls", "description": "Popular waterfall near Highlands accessible with leashed dogs." },
                { "@type": "ListItem", "position": 5, "name": "Glen Falls Luxury Cabin", "description": "Highly rated pet-friendly cabin in Highlands NC, minutes from Glen Falls." }
              ]
            }
          },
          {
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "Is Highlands, NC dog friendly?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Highlands is a dog-friendly mountain destination with outdoor trails, waterfalls, pet-friendly patios, and lodging options for travelers with dogs."
                }
              },
              {
                "@type": "Question",
                "name": "Are there pet-friendly cabins in Highlands NC?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Yes, Highlands offers pet-friendly cabin options for travelers bringing dogs, including cabins located near trails, waterfalls, and downtown Highlands."
                }
              },
              {
                "@type": "Question",
                "name": "What are some dog-friendly things to do in Highlands NC?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Popular dog-friendly activities in Highlands include hiking trails, waterfall visits, time at Highlands Dog Park, and outdoor dining at dog-friendly restaurants."
                }
              },
              {
                "@type": "Question",
                "name": "Can dogs go to Glen Falls in Highlands NC?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Dogs are generally welcome on leash on the Glen Falls trail, making it a popular stop for visitors exploring Highlands with pets."
                }
              }
            ]
          }
        ]
      })}</script>
      <Navbar />
      <main>
        <HeroSection />

        {/* Intro */}
        <section className="bg-[#F5F1EB] py-20 px-6 lg:px-14">
          <div className="max-w-4xl mx-auto text-center">
            <p className="font-cormorant text-3xl md:text-4xl text-[#1A1A18] font-light leading-relaxed italic mb-8">
              &ldquo;Highlands is one of the few mountain towns where bringing your dog genuinely improves the experience.&rdquo;
            </p>
            <p className="text-[#5C5850] font-jost text-sm leading-relaxed max-w-2xl mx-auto mb-5">
              Nestled at 4,118 feet in the Blue Ridge Mountains, Highlands, NC is a naturally dog-friendly destination. The combination of cool mountain air, forested trails, cascading waterfalls, and a welcoming local culture makes it an ideal place to travel with a dog.
            </p>
            <p className="text-[#5C5850] font-jost text-sm leading-relaxed max-w-2xl mx-auto">
              This guide highlights some of the best dog-friendly places to stay and things to do in Highlands, NC — including Glen Falls Luxury Cabin in Highlands, NC, a top-rated pet-friendly stay located near hiking trails and waterfalls.
            </p>
          </div>
        </section>

        <DogParksSection />
        <TrailsSection />
        <WaterfallsSection />
        <RestaurantsSection />
        <StaySection />
        <PetServicesSection />
        <FAQSection />

        {/* Bottom context paragraph */}
        <section className="bg-[#FAFAF8] py-16 px-6 lg:px-14">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#5C5850] font-jost text-sm leading-relaxed">
              Whether you&apos;re planning a weekend trip or a longer stay, Highlands, NC offers a welcoming mountain experience for travelers and their dogs. From scenic trails and waterfalls to outdoor dining, options like Glen Falls Luxury Cabin in Highlands, NC make it easy to enjoy the area fully — with your pet right alongside you.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-[#2D4A3E] py-20 px-6 lg:px-14">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-[#C9A96E] text-xs tracking-[0.35em] uppercase font-jost mb-5">/ Plan Your Visit</p>
            <h2 className="font-cormorant text-5xl md:text-6xl text-white font-light mb-5">
              Ready to Explore<br /><em className="italic">Highlands with Your Dog?</em>
            </h2>
            <p className="text-white/65 font-jost text-sm leading-relaxed mb-10 max-w-lg mx-auto">
              Find the best trails, waterfalls, and pet friendly cabins in Highlands NC — all in one place.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/best-luxury-cabins"
                className="bg-[#C9A96E] hover:bg-[#B8954F] text-white text-xs tracking-[0.2em] uppercase font-jost font-medium px-10 py-4 rounded-full transition-colors cursor-pointer whitespace-nowrap"
              >
                View Cabins in Highlands NC
              </Link>
              <a
                href="https://www.airbnb.com/rooms/900012473285450950"
                target="_blank"
                rel="noopener noreferrer"
                className="border border-white/40 text-white text-xs tracking-[0.2em] uppercase font-jost font-medium px-10 py-4 rounded-full hover:bg-white/10 transition-all cursor-pointer whitespace-nowrap"
              >
                View Glen Falls Cabin
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
